import React, { useEffect, useState } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  DatePicker,
  notification,
  Spin,
  Select,
} from "antd";
import { FaArrowLeft, FaSearch, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const { RangePicker } = DatePicker;
const { Option } = Select;

const components = {
  header: {
    cell: (props) => (
      <th
        {...props}
        style={{
          ...props.style,
          backgroundColor: "#daeffe",
        }}
      />
    ),
  },
};

const GroupWiseMonthlySalesReport = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [monthKeys, setMonthKeys] = useState([
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ]);

  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [downloading, setDownloading] = useState(false);

  //  NEW: FY dropdown like property-wise
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());

  // Optional: date range override
  const [dateRange, setDateRange] = useState(null);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [salesmen, setSalesmen] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [salesmenLoading, setSalesmenLoading] = useState(true);
  const [selectedPropertyGroup, setSelectedPropertyGroup] = useState(undefined);
  const [propertyGroupOptions, setPropertyGroupOptions] = useState([]);

  const [summary, setSummary] = useState({});
  const formatCurrency = (amount) => {
    if (amount == null) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  };
  useEffect(() => {
    const fetchSalesmen = async () => {
      setSalesmenLoading(true);
      try {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/listSalesPersons?page=1&limit=50`,
          {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          },
        );
        const j = await res.json();
        if (j.success && Array.isArray(j.data)) {
          const seen = new Set();
          const opts = j.data
            .map((s) => String(s?.name ?? "").trim())
            .filter((name) => {
              if (!name) return false;
              if (seen.has(name)) return false;
              seen.add(name);
              return true;
            })
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ label: name, value: name }));
          setSalesmen(opts);
        }
        else
          notification.error({
            message: "Failed",
            description: j.message || "Failed to fetch salesmen",
          });
      } catch (e) {
        console.error(e);
        notification.error({
          message: "Error",
          description: "Failed to fetch salesmen",
        });
      } finally {
        setSalesmenLoading(false);
      }
    };

    fetchSalesmen();
  }, []);

  const beginRequest = useLatestRequest();

  const fetchData = async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const params = new URLSearchParams();

      //  Send FY always (backend will use it when dateRange not present)
      if (financialYear) params.set("financialYear", financialYear);
      if (selectedPropertyGroup)
        params.set("propertyGroup", selectedPropertyGroup);

      //  If dateRange selected, it overrides FY in backend
      if (dateRange && dateRange.length === 2) {
        params.set(
          "startDate",
          moment(dateRange[0].toDate()).format("DD-MM-YYYY"),
        );

        params.set(
          "endDate",
          moment(dateRange[1].toDate()).format("DD-MM-YYYY"),
        );
      }
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      if (selectedSalesman) params.set("createdBy", selectedSalesman);
      const q = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getGroupWiseMonthlySales${q}`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        },
      );

      const j = await res.json();
      if (!isCurrent()) return;

      if (j.success) {
        setData(j.data || []);
        setSummary(j.grandTotal || {});
        if (Array.isArray(j.monthKeys) && j.monthKeys.length)
          setMonthKeys(j.monthKeys);

      }

      const groups = Array.from(
        new Set((j.data || []).map((r) => r.groupName)),
      ).filter(Boolean);
      groups.sort((a, b) => String(a).localeCompare(String(b)));
      setPropertyGroupOptions(groups.map((g) => ({ label: g, value: g })));
    }

    catch (err) {
      if (!isCurrent()) return;
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to fetch report",
      });
      setData([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, financialYear, selectedSuffix, selectedSalesman, selectedPropertyGroup]);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(data);
      return;
    }
    const s = searchText.toLowerCase().trim();
    setFilteredData(
      data.filter((r) =>
        String(r.groupName || "")
          .toLowerCase()
          .includes(s),
      ),
    );
  }, [data, searchText]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();

      if (financialYear) params.set("financialYear", financialYear);
      if (selectedPropertyGroup)
        params.set("propertyGroup", selectedPropertyGroup);

      if (dateRange && dateRange.length === 2) {
        params.set(
          "startDate",
          moment(dateRange[0].toDate()).format("DD-MM-YYYY"),
        );
        params.set(
          "endDate",
          moment(dateRange[1].toDate()).format("DD-MM-YYYY"),
        );
      }
      if (selectedPropertyGroup)
        params.set("propertyGroup", selectedPropertyGroup);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      if (selectedSalesman) params.set("createdBy", selectedSalesman);

      params.set("excel", "true");

      const q = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getGroupWiseMonthlySales${q}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
          },
        },
      );

      if (!res.ok) {
        let err = "Failed to download";
        try {
          const j = await res.json();
          err = j.message || err;
        } catch { }
        notification.error({ message: "Error", description: err });
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      a.download = `Group_Wise_Monthly_Sales_${dateRange && dateRange.length === 2
          ? moment(dateRange[0].toDate()).format("YYYYMMDD") +
          "_" +
          moment(dateRange[1].toDate()).format("YYYYMMDD")
          : financialYear || "report"
        }.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to download excel.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      title: "Group",
      dataIndex: "groupName",
      key: "groupName",
      fixed: "left",
      width: 220,
      render: (t) => <span style={{ fontWeight: 600 }}>{t || "N/A"}</span>,
    },
    ...monthKeys.map((mk) => ({
      title: mk,
      dataIndex: mk,
      key: mk,
      align: "center",
      width: 100,
      render: (v) => <span>{v ? formatCurrency(v) : "₹0"}</span>,
    })),
    {
      title: "Total",
      dataIndex: "Total",
      key: "Total",
      fixed: "right",
      width: 140,
      align: "center",
      render: (v) => (
        <span style={{ fontWeight: 700 }}>{formatCurrency(v)}</span>
      ),
    },
  ];

  const dataWithSummary = [
    ...filteredData,
    {
      key: "summary",
      groupName: "Grand Total",
      ...summary,
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Group Wise Monthly Sales
            </Space>

            <Space >
              {/*  FY dropdown */}
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 130 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Property Group"
                allowClear
                showSearch
                value={selectedPropertyGroup}
                onChange={setSelectedPropertyGroup}
                options={propertyGroupOptions}
                style={{ width: 170 }}
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "")
                    .toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
              />

              <Select
                mode="multiple"
                placeholder="Filter by Suffix"
                allowClear
                value={
                  Array.isArray(selectedSuffix) && selectedSuffix.length
                    ? selectedSuffix
                    : undefined
                }
                onChange={(val) => setSelectedSuffix(val ?? [])}
                options={DEFAULT_SUFFIX_OPTIONS}
                style={{ minWidth: 160 }}
                dropdownRender={selectAllDropdownRender(
                  DEFAULT_SUFFIX_OPTIONS,
                  Array.isArray(selectedSuffix) ? selectedSuffix : [],
                  setSelectedSuffix,
                )}
              />
              <Select
                value={selectedSalesman}
                onChange={setSelectedSalesman}
                style={{ width: 180 }}
                placeholder="Filter by Salesman"
                loading={salesmenLoading}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "")
                    .toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                options={salesmen}
              >
              </Select>

              {/* Date range overrides FY */}
              <RangePicker
                value={dateRange}
                onChange={(d) => setDateRange(d)}
                format={"DD-MM-YYYY"}
                allowClear
                style={{ width: 220 }}
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={dataWithSummary}
            rowKey={(r) => r.groupName || r.key}
            pagination={false}
            scroll={{ x: Math.max(1200, monthKeys.length * 100), y: 600 }}
            rowClassName={(record) =>
              record.groupName === "Grand Total" ? "total-row" : ""
            }
            size="small"
            components={components}
          />
        </Spin>
      </Card>

      <style jsx>{`
        .total-row {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
        .total-row td {
          background-color: #f0f8ff !important;
          font-weight: bold !important;
        }
      `}</style>
    </div>
  );
};

export default GroupWiseMonthlySalesReport;
